import { CustomQuestion, InitialInterviewQuestion, PreInterviewQuestion } from '../../../typings';
import { ActivityType, InitialInterviewQuestionType } from "../../enums";
import { InitialInterviewApiService } from "./initial-interview-api.service";
import { TalentHappyPathDataService } from "../shared/service/talent-happy-path-data-service";
import { Router } from "@angular/router";
import { TalentProfileApiService } from '../talent-profile/talent-profile-api.service';

/**
 * Bank-drawn questions carry the bank id. Ones written before the bank existed only have their theme.
 */
export function isPlaceholderSlotQuestion(customQuestion: CustomQuestion): boolean {
  return customQuestion.behavioralQuestionBankId != null
    || customQuestion.theme === 'Standard Interview Question';
}

export function combineQuestions(
  preInterviewQuestions: PreInterviewQuestion[] = [],
  customQuestions: CustomQuestion[] = []
): InitialInterviewQuestion[] {
  const initialInterviewQuestions: InitialInterviewQuestion[] = [];

  // Map CustomQuestions to InitialInterviewQuestion
  customQuestions.forEach((customQuestion) => {
    initialInterviewQuestions.push({
      questionId: customQuestion.questionId,
      question: customQuestion.question,
      typeId: customQuestion.typeId,
      submitted: customQuestion.submitted,
      answer: customQuestion.answer,
      initialInterviewQuestionType: InitialInterviewQuestionType.CUSTOM_QUESTION,
      optionChoiceList: [],
      isStandardQuestion: isPlaceholderSlotQuestion(customQuestion)
    });
  });

  // Map PreInterviewQuestion to InitialInterviewQuestion
  preInterviewQuestions.forEach((preQuestion) => {
    initialInterviewQuestions.push({
      questionId: preQuestion.orderPreinterviewQuestionId,
      question: preQuestion.description,
      typeId: preQuestion.questionTypeId,
      submitted: preQuestion.submitted,
      answer: preQuestion.answer,
      initialInterviewQuestionType: InitialInterviewQuestionType.PRE_INTERVIEW_QUESTION,
      optionChoiceList: preQuestion.optionChoiceList,
      isStandardQuestion: false
    });
  });

  return initialInterviewQuestions;
}



export function initialInterviewClickIllDoThisLater(navigationPath: string,
                                                    talent: Talent,
                                                    jobPostingId: number,
                                                    orderId: number,
                                                    jobTitle: string,
                                                    initialInterviewApi: InitialInterviewApiService,
                                                    talentHappyPathDataService: TalentHappyPathDataService,
                                                    router: Router,
                                                    gatherUUID: string = "") {
  initialInterviewApi.createTalentProfileActivity(
    ActivityType.IIQ_DO_THIS_LATER_CLICKED, talent.personId, orderId, jobPostingId
  );

  // One reminder per application: the video interview can come before or after the IIQ, and either
  // may have sent it already. Marked before the request so a quick second click cannot send again.
  if (talentHappyPathDataService.doThisLaterEmailSent) {
    console.info('Email reminder was skipped since it was already sent for this job');
  } else {
    talentHappyPathDataService.doThisLaterEmailSent = true;
    initialInterviewApi.sendDoThisLaterEmail(
      jobPostingId,
      talent.personId,
      talent.firstName,
      talent.lastName,
      talent.email,
      jobTitle,
      gatherUUID
    ).subscribe(() => {
      console.log("Talent Do It Later Email sent successfully from IIQ.")
    }, error => {
      talentHappyPathDataService.doThisLaterEmailSent = false;
      console.error("Error saving Talent Do It Later emails from IIQ: ", error);
    });
  }

  if (navigationPath.length > 0) {
    router.navigate([navigationPath], { skipLocationChange: true });
  }
}

export function routeGatherToProfileOrThankYou(talentPersonId: number,
                                               talentProfileApiService: TalentProfileApiService,
                                               gatherRouter: Router) {
  talentProfileApiService.talentProfileCompleted(talentPersonId).subscribe(isCompleted => {
    if(isCompleted) {
      void gatherRouter.navigateByUrl('/thank-you', { skipLocationChange: true });
    }
    else {
      talentProfileApiService.readEeocData(talentPersonId).subscribe(
        eeocData => {
          let hasAnsweredAtLeastOneQuestion = Object.values(eeocData.eeoc).some(eeocValue => !!eeocValue);
          if (hasAnsweredAtLeastOneQuestion) {
            void gatherRouter.navigateByUrl('/thank-you', { skipLocationChange: true });
          } else {
            void gatherRouter.navigate(['/talent-profile'], { skipLocationChange: true });
          }
        }
      );
    }
  })
}

/**
 * Compares two numbers with epsilon tolerance to handle floating-point precision issues
 * @param a First number to compare
 * @param b Second number to compare
 * @param epsilon Tolerance value (default: 0.00001)
 * @returns true if numbers are equal within epsilon tolerance, false otherwise
 */
export function isNumbersEqual(a: number, b: number, epsilon: number = 0.00001): boolean {
  // Handle null or undefined values
  if (a === null || a === undefined || b === null || b === undefined) {
    return a === b;
  }

  return Math.abs(a - b) <= epsilon;
}
